<script lang="ts">
  import Portrait from '../../components/portrait.svelte';
  import { createDie } from '../../models/die.type';
  import type { Die as DieObject, DieType } from '../../models/die.type';
  import DieElement from '../../dice-tray/die.svelte';
  import Icon from '../../icon/icon.svelte';
  import Text from '../../components/text.svelte';
  import { tick } from 'svelte';
  import { glossary } from '../../../store';
  import { TraitType } from '../../models/trait-registry';
  import type { PlayerClass } from '../../models/player.type';

  /**
   * Not a Player -- glossary.svelte flattens each class' archetypes into these
   * rows, pairing the archetype with the one weapon, item and ability it draws
   * from its class' three parallel dice lists.
   */
  type HeroEntry = {
    title: string;
    text: string;
    id: TraitType;
    colour?: string;
    classTitle?: string;
    classType?: PlayerClass;
    complexity?: string;
    dice: Array<DieType | undefined>;
  };

  interface Props {
    data: HeroEntry;
  }

  let { data }: Props = $props();

  // Same order, and the same labels, as the Classes section builds them in.
  const SLOTS = ['Weapon', 'Item', 'Ability'];

  let slots: Array<{ label: string; die: DieObject }> = [];

  if (data?.dice) {
    data.dice.forEach((type, index) => {
      // A class whose dice lists run shorter than its archetype list leaves
      // holes here, and createDie would throw on them.
      if (!type) {
        return;
      }
      // Unique per hero and slot. createDie with no id defaults every die to
      // 'x0', so all three of a hero's dice used to share one.
      slots.push({
        label: SLOTS[index] ?? '',
        die: createDie(type, index, `${data.id}-${index}`),
      });
    });
  }

  // Not reactive: slots is built once above and never reassigned.
  const dice = slots.map((slot) => slot.die);

  /**
   * Opens Techniques filtered to what this hero can actually build: the dice go
   * into the store, and technique.svelte hides any recipe whose ingredients
   * aren't all present across their faces.
   *
   * Order matters -- selecting a category clears the search term, so the dice
   * and the term have to be set after it.
   */
  function openTechniques() {
    glossary.selected.set('techniques');
    glossary.searchTerm.set('');
    glossary.searchDie.set(dice);
  }

  /**
   * Jumps back to this hero's parent class. Classes has no search box to narrow
   * with, so the entry is scrolled to directly -- selecting the section has to
   * render it first, hence the tick.
   */
  async function openClass() {
    glossary.selected.set('classes');
    glossary.searchTerm.set('');

    await tick();
    document
      .querySelector(`[data-class-entry="${data.classType}"]`)
      ?.scrollIntoView({ block: 'start' });
  }
</script>

<div class="container entry" style="--class-colour: {data.colour ?? '#87442b'}">
  <div class="portrait">
    <Portrait data={{ type: data.id }}></Portrait>
  </div>

  <div class="information">
    <div class="heading">
      <h2>{data.title}</h2>

      {#if data.complexity}
        <span class="complexity {data.complexity.toLowerCase()}">
          {data.complexity}
        </span>
      {/if}
    </div>

    {#if data.classTitle}
      <button class="class-link" onclick={openClass}>{data.classTitle}</button>
    {/if}

    {#if data.text}
      <Text data={data.text} class="blurb" />
    {/if}

    <div class="loadout">
      {#each slots as slot (slot.die.id)}
        <div class="slot">
          <span class="label">{slot.label}</span>
          <div class="die-container">
            <DieElement die={slot.die} die3D={false} hover={true} sizeMod={1} />
          </div>
        </div>
      {/each}

      {#if dice.length}
        <button class="techniques" onclick={openTechniques}> View Techniques </button>
      {/if}
    </div>

    {#if data.id == TraitType.Knight}
      <ul class="tips">
        <li>
          Use <Icon name="sword" /> to do single large attacks.
        </li>
        <li>
          Use <Icon name="shield" /> to gain Block as temporary health. The knight can store up to 5 block
          between turns.
        </li>
        <li>
          Combine <Icon name="sword" /> and <Icon name="shield" /> symbols to deal damage and gain block
          in the same action.
        </li>
        <li>
          Use <Icon name="fist" /> to gain Strength which increases damage bonuses on attacks.
        </li>
        <li>
          Use <Icon name="bone" /> to gain Armoured to generate armour each round.
        </li>
        <li>
          Use Strike (<Icon name="fist" /><Icon name="sword" />) to deal damage and gain Block equal
          to the damage dealt.
        </li>
        <li>
          Build up Block and the use Shield Bash (<Icon name="fist" /><Icon name="shield" />) to
          deal damage based on your current Block.
        </li>
      </ul>
    {/if}
  </div>
</div>

<!--
  Design notes for the archetypes that still need their tips written. These used
  to sit inside a run of `{#if data.id == '...'}` blocks that rendered nothing
  but the hero's own name back at it; the name is in the heading above now, so
  only the notes are left.

  Warrior - hammer. Use hammer to do heavy attacks that can stun enemies,
    limiting their actions.
  Gladiator - spear & stances
  Barbarian - axe & rage

  Bladestorm - dagger attacks
  Shadow Walker - stealth and backstabs
  Fencer - ripost attacks
  Spell Slinger - imbue attacks with elements

  Dryad - defensive Earth magic
  Anarchist - risky self damaging attacks to gain infusions
  Mana Born - able to use stable attacks
  Wielder - more elemental combinations

  Tamer - wolf companion
  Arbelist - piercing attacks & advanced ammo
  Piper - debuffing attacks and music buffs
  Archer - regenerating ammo and multi-attacks
-->

<style lang="less">
  @import '../../../defines.less';

  .container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-gap: 16px;
    align-items: start;

    text-align: left;

    // No card. The class' colour rides on the frame around the hero's portrait
    // -- see --portrait-accent in components/portrait.svelte -- and the entry's
    // padding and the rule above it come from .entry in glossary.svelte.
    --portrait-accent: var(--class-colour);
    --icon-size: 20px;

    @media @mobile {
      grid-template-columns: minmax(0, 1fr);
      justify-items: center;
      text-align: center;
    }
  }

  .portrait {
    // Portrait sizes itself off --column-size, which is 300px app-wide for the
    // battle screen.
    --column-size: 150px;

    @media @mobile {
      --column-size: 130px;
    }
  }

  .information {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    grid-gap: 8px;
    width: 100%;

    @media @mobile {
      align-items: center;
    }
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    grid-gap: 10px;

    h2 {
      font-size: 1.4rem;
      text-transform: capitalize;
      color: var(--class-colour);
    }
  }

  .complexity {
    padding: 2px 10px;
    font-size: 0.65rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border-radius: 999px;
    border: 1px solid currentColor;
    opacity: 0.8;

    &.simple {
      color: var(--green);
    }
    &.moderate {
      color: var(--yellow);
    }
    &.complex {
      color: var(--orange);
    }
  }

  .class-link {
    // Reset of the global button.less pill -- this one reads as a breadcrumb.
    padding: 0;
    font-size: 0.8rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    background: none;
    box-shadow: none;
    opacity: 0.6;
    transition:
      opacity 0.2s,
      color 0.2s;

    &::before {
      content: '\2039\00a0';
    }

    &:hover,
    &:focus-visible {
      filter: none;
      opacity: 1;
      color: var(--class-colour);
    }

    &:active {
      box-shadow: none;
    }
  }

  .container :global(.blurb) {
    font-size: 0.9rem;
    opacity: 0.85;
  }

  .loadout {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    grid-gap: 12px;
    margin-top: 4px;

    @media @mobile {
      justify-content: center;
    }

    .slot {
      display: flex;
      flex-direction: column;
      align-items: center;
      grid-gap: 4px;

      .label {
        font-size: 0.65rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        opacity: 0.55;
      }

      .die-container {
        position: relative;
      }
    }
  }

  .techniques {
    font-size: 0.8rem;
    padding: 8px 16px;
    border: 1px solid var(--background-colour-light);
    box-shadow: none;
    background: var(--background-colour-dark);
    transition:
      background 0.2s,
      border-color 0.2s,
      color 0.2s;

    &::after {
      content: '\00a0\203A';
      display: inline-block;
      transition: transform 0.2s;
    }

    &:hover,
    &:focus-visible {
      filter: none;
      color: var(--class-colour);
      border-color: var(--class-colour);

      &::after {
        transform: translateX(3px);
      }
    }

    &:active {
      box-shadow: none;
    }
  }

  .tips {
    margin: 8px 0 0;
    padding-left: 20px;
    font-size: 0.9rem;
    line-height: 1.7;
    opacity: 0.85;

    @media @mobile {
      text-align: left;
    }

    li {
      margin-bottom: 4px;
    }
  }
</style>
